import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type SkeletonShape = 'card' | 'list-item' | 'message-bubble' | 'inline-text'

export interface LoadingSkeletonProps {
  shape: SkeletonShape
  count?: number
  className?: string
}

function CardSkeleton() {
  return (
    <div className="rounded-[10px] border border-[color:var(--color-neutral-200)] p-4 space-y-3">
      {/* title bar */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-4 w-1/5 ml-auto" />
      </div>
      {/* body lines */}
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <Skeleton className="h-3 w-3/5" />
      {/* action row */}
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-7 w-20 rounded-full" />
        <Skeleton className="h-7 w-16 rounded-full" />
      </div>
    </div>
  )
}

function ListItemSkeleton() {
  return (
    <div className="flex items-center gap-3 py-2">
      {/* avatar circle */}
      <Skeleton className="h-9 w-9 rounded-full flex-shrink-0" />
      {/* two text lines */}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-3/5" />
        <Skeleton className="h-3 w-2/5" />
      </div>
    </div>
  )
}

function MessageBubbleSkeleton({ index }: { index: number }) {
  const isUser = index % 2 === 0
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('space-y-1.5', isUser ? 'w-2/3' : 'w-3/4')}>
        <Skeleton className="h-4 w-full rounded-[10px]" />
        <Skeleton className={cn('h-4 rounded-[10px]', isUser ? 'w-4/5' : 'w-3/4')} />
        <Skeleton className={cn('h-4 rounded-[10px]', isUser ? 'w-3/5' : 'w-2/3')} />
      </div>
    </div>
  )
}

function InlineTextSkeleton() {
  return <Skeleton className="h-4 w-32 rounded-full inline-block" />
}

function SingleSkeleton({ shape, index }: { shape: SkeletonShape; index: number }) {
  switch (shape) {
    case 'card':
      return <CardSkeleton />
    case 'list-item':
      return <ListItemSkeleton />
    case 'message-bubble':
      return <MessageBubbleSkeleton index={index} />
    case 'inline-text':
      return <InlineTextSkeleton />
  }
}

export function LoadingSkeleton({ shape, count = 1, className }: LoadingSkeletonProps) {
  const { t } = useTranslation()
  const items = Array.from({ length: count }, (_, i) => i)

  return (
    <div
      className={cn(
        shape === 'message-bubble' || shape === 'list-item' || shape === 'card'
          ? 'space-y-4'
          : undefined,
        className,
      )}
      aria-busy="true"
      aria-label={t('common.loading')}
      // Shimmer animation is supplied by shadcn Skeleton (animate-pulse).
      // When prefers-reduced-motion: reduce, Tailwind strips animate-pulse
      // via the motion-safe: / motion-reduce: variants. Skeleton itself uses
      // animate-pulse which is suppressed by Tailwind's generated
      // @media (prefers-reduced-motion: reduce) { .animate-pulse { animation: none } }
    >
      {items.map((i) => (
        <SingleSkeleton key={i} shape={shape} index={i} />
      ))}
    </div>
  )
}
